import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ContextMenuPortal } from './ContextMenuPortal'

export interface ContextMenuActionItem {
  type?: 'item'
  id: string
  label: string
  icon?: ReactNode
  shortcut?: string
  disabled?: boolean
  danger?: boolean
  onSelect?: () => void
}

export interface ContextMenuSeparatorItem {
  type: 'separator'
  id: string
}

export type ContextMenuItem = ContextMenuActionItem | ContextMenuSeparatorItem

interface ContextMenuProps {
  open: boolean
  x: number
  y: number
  items: ContextMenuItem[]
  onClose: () => void
}

const VIEWPORT_MARGIN = 8

export function ContextMenu({ open, x, y, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState({ x, y })

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return

    const menu = menuRef.current
    const rect = menu.getBoundingClientRect()
    const nextX = Math.max(
      VIEWPORT_MARGIN,
      Math.min(x, window.innerWidth - rect.width - VIEWPORT_MARGIN),
    )
    const nextY = Math.max(
      VIEWPORT_MARGIN,
      Math.min(y, window.innerHeight - rect.height - VIEWPORT_MARGIN),
    )

    setPosition({ x: nextX, y: nextY })
  }, [open, x, y, items])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent | PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [onClose, open])

  const content = useMemo(
    () => items.map((item) => {
      if (item.type === 'separator') {
        return (
          <div
            key={item.id}
            className="my-1 h-px"
            role="separator"
            style={{ background: 'var(--border)' }}
          />
        )
      }

      return (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[11px] transition-colors disabled:cursor-default disabled:opacity-45"
          style={{
            color: item.danger ? 'var(--color-danger)' : 'var(--text)',
          }}
          onClick={() => {
            if (item.disabled) return
            item.onSelect?.()
            onClose()
          }}
          onMouseEnter={(event) => {
            if (item.disabled) return
            event.currentTarget.style.background = item.danger
              ? 'var(--color-danger-bg)'
              : 'rgba(99,102,241,.10)'
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent'
          }}
        >
          <span
            aria-hidden="true"
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[12px]"
            style={{ color: item.danger ? 'var(--color-danger)' : 'var(--accent)' }}
          >
            {item.icon ?? ''}
          </span>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.shortcut ? (
            <span
              className="shrink-0 text-[10px]"
              style={{ color: 'var(--text-dim)' }}
            >
              {item.shortcut}
            </span>
          ) : null}
        </button>
      )
    }),
    [items, onClose],
  )

  if (!open) {
    return null
  }

  return (
    <ContextMenuPortal>
      <div
        ref={menuRef}
        role="menu"
        className="fixed z-[70] min-w-[220px] rounded-xl border p-2 shadow-2xl backdrop-blur-sm"
        style={{
          left: position.x,
          top: position.y,
          background: 'color-mix(in srgb, var(--bg-popup) 94%, transparent)',
          borderColor: 'var(--border)',
          color: 'var(--text)',
        }}
      >
        {content}
      </div>
    </ContextMenuPortal>
  )
}
